import * as apiUtil from '../apiUtils';
import * as apiCommon from '../apiCommon';
import { ITEM_LOCATION } from '../../constants/constant_doc_types';
import { STORE } from '../../constants/constant_location_types';
import { INVENTORY_ITEM_TYPES } from '../../constants/constant_item_types';

export function generateItemLocation(app, params) {
  const service = apiCommon.getAccountService(app, 'mass-item-location/generate');
  return service.create(null, params, false, { timeout: 0 });
}
export async function getItemLocation(app, params) {
  const { id, viewMode } = params;
  const docType = ITEM_LOCATION;
  const doc = await apiCommon.getDoc(app, { id, docType }, { assemble_custom_fields: true });

  return apiUtil.filterCustomFieldsByViewMode(doc, null, viewMode);
}

export const fetchItemLocationDropdown = (app, params) => {
  const { searchTerm, searchParams, fields, id } = params;
  const dropdown_type = 'item_location/dropdown';
  const query = {
    id,
    dropdown_type,
    fields,
    searchTerm,
    searchParams
  };

  return apiCommon.getDropdownList(app, { query });
}

export async function getDefaultItemLocation(app, params) {
  const { currencyId, itemId } = params;
  const itemLocParams = {
    item: itemId,
    currency: currencyId,
  };

  const result = await apiCommon.queryDocs(
    app,
    ITEM_LOCATION + '/default',
    itemLocParams,
  );

  return result.errors ? null : result;
}

export async function getUsedLocations(app, params) {
  const { itemId } = params;
  const filters = {
    location_type: { _id: STORE },
    item: { _id: itemId },
  };
  const itemLocationResult = await apiCommon.getListData(app, {
    filters,
    list_type: ITEM_LOCATION,
  });

  const locationIds = itemLocationResult.docs.map((itemLocation) => {
    return itemLocation.location._id;
  });

  return locationIds;
}

export async function deleteItemLocation(app, params) {
  const { data } = params;
  const itemLocationId = data._id || data.id;
  console.log('deleting itemLocationId', itemLocationId);

  const result = await apiCommon.deleteDoc(app, {
    id: itemLocationId,
    docType: ITEM_LOCATION,
  });
  console.log('done deleting itemLocationId', itemLocationId);
  console.log('done deleting result', result);

  const itemTypeId = data.item_type._id;

  if (INVENTORY_ITEM_TYPES.indexOf(itemTypeId) > -1) {
    const subLocationFilters = {
      parent_item_location: {
        _id: itemLocationId,
      },
    };

    const subLocationParams = {
      filters: subLocationFilters,
      list_type: 'sub_item_location',
    };

    const subItemLocationResult = await apiCommon.getListData(
      app,
      subLocationParams,
    );
    console.log('deleting subItemLocationResult', subItemLocationResult);
    await subItemLocationResult.docs.map((subItemLocation) => {
      return apiCommon.deleteDoc(app, {
        id: subItemLocation._id,
        docType: ITEM_LOCATION,
      });
    });
  }

  return result;
}

export async function saveUndefaultItemLocation(app, itemLocationId) {
  if (itemLocationId) {
    const itemLocationService = apiCommon.getAccountService(app, ITEM_LOCATION);
    const updatedItemLocation = await itemLocationService.patch(
      itemLocationId,
      { default: false },
    );
    console.log('savedUndefaultItemLocation', updatedItemLocation);
  }
}

export async function saveItemLocation(app, dataToSave) {
  const { data, user } = dataToSave;

  let itemLocationData = {
    allow_auto_discount: apiUtil.toBoolean(data.allow_auto_discount),
    allow_manual_discount: apiUtil.toBoolean(data.allow_manual_discount),
    allow_tax_exempt: apiUtil.toBoolean(data.allow_tax_exempt),
    available_from: data.available_from
      ? apiUtil.toStringDate(data.available_from)
      : null,
    available_for_pickup: apiUtil.toBoolean(data.available_for_pickup),
    available_for_transfer: apiUtil.toBoolean(data.available_for_transfer),
    available_on_web: apiUtil.toBoolean(data.available_on_web),
    available_to_ship: apiUtil.toBoolean(data.available_to_ship),
    available_to: data.available_to
      ? apiUtil.toStringDate(data.available_to)
      : null,

    charge_amount: data.charge_amount,
    charge_is_pct: Boolean(Number(data.charge_percentage || 0)),
    charge_percentage: data.charge_percentage,
    currency_id: data.currency_id,

    excluded_shipping_methods: apiUtil.toObjectIds(
      data.excluded_shipping_methods,
      data.excluded_shipping_method_ids,
      'shipping_method_id',
    ),

    default_item_location: apiUtil.toBoolean(data.default_item_location),
    default_shipping_method_id: data.default_shipping_method_id,
    discount_amount: data.discount_amount,
    discount_is_pct: Boolean(Number(data.discount_percentage || 0)),
    discount_percentage: data.discount_percentage,
    doc_type: ITEM_LOCATION,

    floor_price: data.floor_price,

    item_id: data.item_id,
    item_type: data.item_type,

    landed_cost: data.landed_cost,
    location_id: data.location_id,
    location_type: data.location_type,
    loyalty_multiplier: apiUtil.toDecimal(data.loyalty_multiplier),
    loyalty_type: apiUtil.toObjectId(data.loyalty_type),

    markdown_price: data.markdown_price,
    max_reorder_point: data.max_reorder_point,
    min_reorder_point: data.min_reorder_point,

    output_device_types: apiUtil.toObjectIds(
      data.output_device_types,
      data.output_device_type_ids,
      'device_type_id',
    ),

    parent_item_type: data.parent_item_type,
    price_book_reference: data.price_book_reference || null,
    purchase_cost: data.purchase_cost,

    safety_stock: data.safety_stock,
    shipping_cost: data.shipping_cost,
    shipping_handling: data.shipping_handling,
    subsidiary_id: data.subsidiary_id,
    suppliers: data.suppliers,
    supplier_retail_price: data.supplier_retail_price,

    tax_group_id: data.tax_group_id,
    reorder: apiUtil.toBoolean(data.reorder),
    reserved: data.reserved,

    custom_fields: data.custom_fields,
  };

  const params = {
    source: data,
    doc: itemLocationData,
    user,
  };

  if (data.id) {
    const updatedItemLocation = await apiCommon.saveDoc(app, params);
    return updatedItemLocation;
  } else {
    const savedItemLocation = await apiCommon.saveDoc(app, params);
    return savedItemLocation;
  }
}

export async function getItemLocationByItemIdLocationId(app, params) {
  const { itemId, locationId, ...otherParams } = params;

  if (!itemId || !locationId) throw new Error('Item ID and Location ID are required');

  const service = app.service('item_location');
  const data = service.get(`${itemId}:${locationId}`, otherParams, undefined, {
    baseURL: `${app.baseUrl}/item_location`,
  });

  return data;
}